/**
 * GoHighLevel Integration
 *
 * This sends quiz data to your GHL webhook/form endpoint.
 * Replace GHL_WEBHOOK_URL with your actual GoHighLevel webhook URL.
 */

interface QuizSubmissionData {
  // Contact Info
  firstName: string
  lastName: string
  email: string
  phone: string
  smsConsent: boolean

  // Quiz Answers
  industry: string
  industryOther?: string
  isOwner: string
  state: string
  yearsInBusiness: string
  revenue: string
  profit: string
  locations: string
  providers: string
  ownerHours: string
  payerMix: string
  concentration: string
  trend: string
  timeline: string

  // Calculated Results (optional - send after calculation)
  valuationLow?: number
  valuationHigh?: number
  multipleRange?: string
  qualityScore?: number

  // Metadata
  submittedAt: string
  source: string
}

/**
 * Send quiz data to GoHighLevel
 *
 * SETUP INSTRUCTIONS:
 * 1. In GHL, go to Settings > Integrations > Webhooks
 * 2. Create a new webhook or use an existing form webhook URL
 * 3. Copy the webhook URL and paste it below
 * 4. Map the fields in GHL to match the data structure
 */
const GHL_WEBHOOK_URL = 'YOUR_GHL_WEBHOOK_URL_HERE'
// Example: 'https://services.leadconnectorhq.com/hooks/abc123xyz/webhook-id'

export async function sendToGoHighLevel(
  data: QuizSubmissionData,
): Promise<boolean> {
  // Skip if webhook URL not configured
  if (!GHL_WEBHOOK_URL || GHL_WEBHOOK_URL === 'YOUR_GHL_WEBHOOK_URL_HERE') {
    console.warn('GHL webhook not configured. Skipping submission.')
    return false
  }

  try {
    console.log('Sending data to GoHighLevel...', data)

    const response = await fetch(GHL_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      throw new Error(
        `GHL webhook failed: ${response.status} ${response.statusText}`,
      )
    }

    console.log('Successfully sent data to GoHighLevel')
    return true
  } catch (error) {
    console.error('Error sending to GoHighLevel:', error)
    // Don't throw - we don't want to break the user experience if GHL is down
    return false
  }
}

/**
 * Alternative: Send to GHL Form (if using form submission instead of webhook)
 *
 * If you're using a GHL form URL instead of a webhook, use this function.
 * GHL forms expect FormData instead of JSON.
 */
export async function sendToGoHighLevelForm(
  data: QuizSubmissionData,
): Promise<boolean> {
  const GHL_FORM_URL = 'YOUR_GHL_FORM_URL_HERE'

  if (!GHL_FORM_URL || GHL_FORM_URL === 'YOUR_GHL_FORM_URL_HERE') {
    console.warn('GHL form URL not configured. Skipping submission.')
    return false
  }

  try {
    const formData = new FormData()

    // Map quiz data to form fields
    formData.append('first_name', data.firstName)
    formData.append('last_name', data.lastName)
    formData.append('email', data.email)
    formData.append('phone', data.phone)
    formData.append('industry', data.industry)
    if (data.industryOther) formData.append('industryOther', data.industryOther)
    formData.append('is_owner', data.isOwner)
    formData.append('state', data.state)
    formData.append('yearsInBusiness', data.yearsInBusiness)
    formData.append('revenue', data.revenue)
    formData.append('profit', data.profit)
    formData.append('locations', data.locations)
    formData.append('providers', data.providers)
    formData.append('owner_hours', data.ownerHours)
    formData.append('payerMix', data.payerMix)
    formData.append('concentration', data.concentration)
    formData.append('trend', data.trend)
    formData.append('timeline', data.timeline)

    if (data.valuationLow)
      formData.append('valuation_low', data.valuationLow.toString())
    if (data.valuationHigh)
      formData.append('valuation_high', data.valuationHigh.toString())
    if (data.multipleRange)
      formData.append('multiple_range', data.multipleRange)
    if (data.qualityScore)
      formData.append('quality_score', data.qualityScore.toString())

    const response = await fetch(GHL_FORM_URL, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`GHL form submission failed: ${response.status}`)
    }

    console.log('Successfully submitted to GHL form')
    return true
  } catch (error) {
    console.error('Error submitting to GHL form:', error)
    return false
  }
}
